// src/layouts/HeaderQuickSearch.jsx
// Ô tìm kiếm nhanh: gõ tên chức năng để nhảy thẳng tới trang (Ctrl + K).

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, CornerDownLeft } from "lucide-react";
import submenu, { moduleMeta } from "./submenuConfig";
import { menuItemHasReadAccess } from "../utils/permissionUtils";

/** Bỏ dấu tiếng Việt để gõ "chamcong" vẫn ra "Chấm công". */
const normalize = (str = "") =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/\s+/g, "");

const MAX_RESULTS = 8;

export default function HeaderQuickSearch() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  const allPages = useMemo(() => {
    const modules = JSON.parse(localStorage.getItem("modules") || "[]");
    const allowedIds = modules.map((m) => m.id);
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    const isSuperAdmin =
      currentUser?.roles?.some((r) => r?.toLowerCase() === "super admin") || false;

    return moduleMeta.flatMap((group) => {
      const visible = group.superOnly ? isSuperAdmin : allowedIds.includes(group.key);
      if (!visible) return [];

      return (submenu[group.key] || [])
        .filter((item) => item.enabled !== false && menuItemHasReadAccess(item))
        .map((item) => ({
          path: `/${group.key}/${item.key}`,
          label: item.label,
          moduleLabel: group.label,
          icon: item.icon,
          haystack: normalize(`${group.label}${item.label}`),
        }));
    });
  }, []);

  const results = useMemo(() => {
    const q = normalize(keyword);
    if (!q) return [];
    return allPages.filter((p) => p.haystack.includes(q)).slice(0, MAX_RESULTS);
  }, [keyword, allPages]);

  useEffect(() => setHighlight(0), [keyword]);

  useEffect(() => {
    const handleShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const goTo = (page) => {
    if (!page) return;
    setKeyword("");
    setOpen(false);
    inputRef.current?.blur();
    navigate(page.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!results.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((prev) => (prev + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((prev) => (prev - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      goTo(results[highlight]);
    }
  };

  return (
    <div className="relative max-w-md">
      <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-red-100 transition-all">
        <Search size={15} className="shrink-0 text-gray-400" />
        <input
          ref={inputRef}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Tìm chức năng..."
          className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-gray-400"
        />
        <kbd className="shrink-0 rounded-md border border-gray-200 px-1.5 py-0.5 text-[10px] font-bold text-gray-400">
          Ctrl K
        </kbd>
      </div>

      <AnimatePresence>
        {open && keyword && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-white py-2 shadow-2xl"
            >
              {results.length === 0 ? (
                <div className="px-4 py-3 text-[12px] text-gray-400">
                  Không tìm thấy chức năng phù hợp
                </div>
              ) : (
                results.map((page, index) => {
                  const Icon = page.icon;
                  return (
                    <button
                      key={page.path}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => goTo(page)}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                        index === highlight ? "bg-red-50" : "hover:bg-gray-50"
                      }`}
                    >
                      {Icon && (
                        <Icon
                          size={15}
                          className={index === highlight ? "text-red-600" : "text-gray-400"}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[13px] font-semibold text-gray-800">
                          {page.label}
                        </div>
                        <div className="truncate text-[10px] font-bold uppercase tracking-widest text-gray-400">
                          {page.moduleLabel}
                        </div>
                      </div>
                      {index === highlight && (
                        <CornerDownLeft size={13} className="shrink-0 text-gray-300" />
                      )}
                    </button>
                  );
                })
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
